import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import type {
  Testimonial,
  CreateTestimonialRequest,
  UpdateTestimonialRequest,
} from '../../services/unified/testimonialsModule';

// Query keys for testimonials
export const testimonialKeys = {
  all: ['testimonials'] as const,
  admin: () => [...testimonialKeys.all, 'admin'] as const,
  candidate: (candidateId: string) => [...testimonialKeys.all, 'candidate', candidateId] as const,
  user: () => [...testimonialKeys.all, 'user'] as const,
  approved: () => [...testimonialKeys.all, 'approved'] as const,
  featured: () => [...testimonialKeys.all, 'featured'] as const,
};

/**
 * Query hook for getting all testimonials (admin)
 */
export const useAdminTestimonials = () => {
  return useQuery({
    queryKey: testimonialKeys.admin(),
    queryFn: (): Promise<Testimonial[]> => {
      return unifiedApiService.testimonials.getAllTestimonials();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Query hook for getting candidate testimonials (admin)
 */
export const useCandidateTestimonials = (candidateId: string) => {
  return useQuery({
    queryKey: testimonialKeys.candidate(candidateId),
    queryFn: (): Promise<Testimonial[]> => {
      return unifiedApiService.testimonials.getCandidateTestimonials(candidateId);
    },
    enabled: !!candidateId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Query hook for getting user's own testimonials
 */
export const useUserTestimonials = () => {
  return useQuery({
    queryKey: testimonialKeys.user(),
    queryFn: (): Promise<Testimonial[]> => {
      return unifiedApiService.testimonials.getUserTestimonials();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook for getting approved testimonials (public)
 */
export const useApprovedTestimonials = () => {
  return useQuery({
    queryKey: testimonialKeys.approved(),
    queryFn: (): Promise<Testimonial[]> => {
      return unifiedApiService.testimonials.getApprovedTestimonials();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook for getting featured testimonials (public)
 */
export const useFeaturedTestimonials = () => {
  return useQuery({
    queryKey: testimonialKeys.featured(),
    queryFn: (): Promise<Testimonial[]> => {
      return unifiedApiService.testimonials.getFeaturedTestimonials();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook for getting public testimonials (approved and featured)
 * This is the main hook for the home page
 */
export const usePublicTestimonials = () => {
  return useQuery({
    queryKey: [...testimonialKeys.all, 'public'],
    queryFn: (): Promise<Testimonial[]> => {
      return unifiedApiService.testimonials.getApprovedTestimonials();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Mutation hook for approving/unapproving a testimonial
 */
export const useApproveTestimonial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approved }: { id: string; approved: boolean }) => {
      return unifiedApiService.testimonials.approveTestimonial(id, approved);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testimonialKeys.all });
    },
  });
};

/**
 * Mutation hook for featuring/unfeaturing a testimonial
 */
export const useFeatureTestimonial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, featured }: { id: string; featured: boolean }) => {
      return unifiedApiService.testimonials.featureTestimonial(id, featured);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testimonialKeys.all });
    },
  });
};

/**
 * Mutation hook for creating a testimonial
 */
export const useCreateTestimonial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTestimonialRequest) => {
      return unifiedApiService.testimonials.createTestimonial(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testimonialKeys.all });
    },
  });
};

/**
 * Mutation hook for updating a testimonial
 */
export const useUpdateTestimonial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTestimonialRequest }) => {
      return unifiedApiService.testimonials.updateTestimonial(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testimonialKeys.all });
    },
  });
};

/**
 * Mutation hook for deleting a testimonial
 */
export const useDeleteTestimonial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      return unifiedApiService.testimonials.deleteTestimonial(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testimonialKeys.all });
    },
  });
};
